import React, { useContext } from "react";
import {
  AccountsFields,
  AddressFields,
  BillingTermsFields,
  BranchField,
  ClientPhoneField,
  ContactPersonField,
  EmailField,
  NameField,
  OperationsTeamField,
  PoliciesField,
  RegistrationCertificateField,
  RegistrationNumberField,
  ServiceTeamField,
  TaxRegistrationField,
  WebsiteField,
} from "../CreateEditClient/CreateEditClientForm";
import {
  ADDRESS_FIELDS,
  EditPencil,
  IncompleteBadge,
  RichTextView,
  SectionEditModal,
  useClientSectionForm,
} from "../ClientSections";
import BaseLoader from "../../../components/ui/BaseLoader";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import InfoCard from "../../../components/ui/InfoCard";
import FileChip from "../../../components/ui/FileChip";
import useAuth from "../../../hooks/useAuth";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { updateClient, updateOwnEntity } from "../../../services/api/client";
import { LayoutContext } from "../../../context/LayoutContext";
import { ModuleType, UserType } from "../../../utils/enum";
import { formatPhoneNumber } from "../../../utils/helpers";
import { childValidationSchema } from "../CreateEditClient";

const SECTIONS = {
  information: {
    title: "Entity Information",
    fields: ["clientName", "email", "phone", "manager", "website"],
    Fields: ({ ctx }) => (
      <>
        <NameField ctx={ctx} />
        <EmailField ctx={ctx} />
        <ClientPhoneField ctx={ctx} />
        <ContactPersonField ctx={ctx} />
        <WebsiteField ctx={ctx} />
      </>
    ),
  },
  teams: {
    title: "Branch & Teams",
    fields: ["branchId", "teamId", "secondaryTeamId"],
    Fields: ({ ctx }) => (
      <>
        <BranchField ctx={ctx} />
        <ServiceTeamField ctx={ctx} />
        <OperationsTeamField ctx={ctx} />
      </>
    ),
  },
  address: { title: "Address", fields: ADDRESS_FIELDS, Fields: AddressFields },
  registration: {
    title: "Registration",
    fields: ["registrationNumber", "registrationCertificate", "taxRegistrationNumber"],
    Fields: ({ ctx }) => (
      <>
        <RegistrationNumberField ctx={ctx} />
        <RegistrationCertificateField ctx={ctx} />
        <TaxRegistrationField ctx={ctx} />
      </>
    ),
  },
  accounts: {
    title: "Accounts",
    fields: [
      "accountsPerson",
      "accountsEmail",
      "accountsPhone",
      "paymentTerms",
      "taxType",
      "taxPercentage",
    ],
    Fields: ({ ctx }) => (
      <>
        <AccountsFields ctx={ctx} />
        {ctx.showInternal && <BillingTermsFields ctx={ctx} />}
      </>
    ),
  },
  policies: { title: "Entity's Policies", fields: ["policies"], Fields: PoliciesField },
};

const EntityProfile = ({ clientId }) => {
  const { isBannerVisible } = useContext(LayoutContext);
  const { auth, authRole, getPermissionForModule } = useAuth();
  const { userData } = useCurrentUser();
  const { hasUpdateAccess } = getPermissionForModule(ModuleType.CLIENT_CUSTOMER);
  const isCustomer = authRole === UserType.CUSTOMER;
  // Clients and admins manage entities; so does the customer owner, for its own
  // entities, through the customer route (the API refuses anyone else's). Branch,
  // teams and billing terms are internal, so a customer never sees them.
  const isCustomerOwner =
    isCustomer && Boolean(auth?.user?.isCustomerOwner ?? userData?.isCustomerOwner);
  const canEdit =
    (authRole === UserType.CLIENT && hasUpdateAccess) ||
    authRole === UserType.ADMIN ||
    isCustomerOwner;
  const showInternal = !isCustomer;

  const sectionForm = useClientSectionForm({
    clientId,
    sections: SECTIONS,
    schema: childValidationSchema,
    save: isCustomer ? updateOwnEntity : updateClient,
  });
  const { client: entity, account, isLoading, openSection, incompleteSections } = sectionForm;

  if (isLoading) return <BaseLoader isLoading />;
  if (!entity) return null;

  const pencil = (key) => canEdit && <EditPencil onClick={() => openSection(key)} />;
  const badge = (key) => canEdit && incompleteSections.has(key) && <IncompleteBadge />;

  return (
    <div className={`bg-light h-full relative ${isBannerVisible ? "top-[107px]" : "top-[56px]"}`}>
      <div className="border-b border-zinc-300 bg-white w-full sticky top-0 z-10">
        <BaseWrapper>
          <h3 className="font-light py-[7px]">{entity.clientName || "Entity"} Details</h3>
        </BaseWrapper>
      </div>

      <BaseWrapper className="pt-4 pb-6">
        <InfoCard
          title="Entity Information"
          badge={badge("information")}
          actions={pencil("information")}
          items={[
            ["Entity Name", entity.clientName],
            ["Contact Person", entity.managerName],
            ["Email", entity.email],
            ["Phone", formatPhoneNumber(entity.phoneNumber)],
            ["Website", entity.website],
          ]}
        />

        {showInternal && (
          <InfoCard
            title="Branch & Teams"
            badge={badge("teams")}
            actions={authRole === UserType.CLIENT && pencil("teams")}
            items={[
              ["Branch", entity.branch?.name],
              ["Service Delivery Team", entity.team?.name],
              ["Operations Team", entity.secondaryTeam?.name],
            ]}
          />
        )}

        <InfoCard
          title="Address"
          badge={badge("address")}
          actions={pencil("address")}
          items={[
            ["Address", entity.address?.addressLine1],
            ["City", entity.address?.city],
            ["State", entity.address?.state],
            ["Zip / Post Code", entity.address?.zipCode],
            ["Country", entity.address?.country],
          ]}
        />

        <InfoCard
          title="Registration"
          badge={badge("registration")}
          actions={pencil("registration")}
          items={[
            ["Registration Number", entity.registrationNumber],
            ["Registration Certificate", <FileChip file={entity.registrationCertificate} />],
            ["VAT/GST Registration", entity.taxRegistrationNumber],
          ]}
        />

        <InfoCard
          title="Accounts"
          badge={badge("accounts")}
          actions={pencil("accounts")}
          items={[
            ["Accounts Person", account?.accountsPerson],
            ["Accounts Email", account?.email],
            ["Accounts Phone", formatPhoneNumber(account?.phone)],
            ...(showInternal
              ? [
                  ["Payment Terms", account?.paymentTerms],
                  ["Tax Type", account?.taxType],
                  [
                    "Tax Percentage",
                    account?.taxPercentage != null ? `${account.taxPercentage}%` : null,
                  ],
                ]
              : []),
          ]}
        />

        {/* Policies are the servicing company's to write; the customer only reads them. */}
        <InfoCard
          title="Entity's Policies"
          badge={showInternal && badge("policies")}
          actions={showInternal && pencil("policies")}
        >
          <RichTextView value={entity.policies} className="pt-4 pb-2" />
        </InfoCard>
      </BaseWrapper>

      {canEdit && (
        <SectionEditModal
          sectionForm={sectionForm}
          formProps={{
            recordLabel: "Entity",
            linkedBranch: entity.branch,
            linkedTeam: entity.team,
            linkedSecondaryTeam: entity.secondaryTeam,
          }}
        />
      )}
    </div>
  );
};

export default EntityProfile;
